/**
 * Football domain tools — Phase 13 P2.
 *
 * Domain-specific tools live under
 * `core/ui/learner-app/src/designer/domain_tools/<domain_id>/`. Each
 * domain pack exports a single `domainTools` array that the
 * pack's bootstrap entry passes to `bootstrapDesignerToolbox` via
 * the `extraTools` arg.
 *
 * Why domain tools live here (not inside the football scenario
 * code) is so the dev surface for tools is uniform — every tool
 * authored in the platform follows the same DesignerTool contract
 * and registers through the same registry. Domain packs declare
 * the capabilities their scene types support; tools bind on those
 * capabilities; the framework decides the rest.
 *
 * The five football tools, in order:
 *
 *   1. RouteAuthor       — CANVAS_OVERLAY; binds on
 *                          `domain=football && actor_movement`.
 *                          Authors actor route sequences post-snap.
 *   2. CoveragePicker    — RIGHT_INSP; binds on
 *                          `domain=football && needs_formation`.
 *                          Picks defensive coverage from
 *                          coverage_catalog.
 *   3. FormationOverlay  — CANVAS_OVERLAY; binds on
 *                          `domain=football && needs_formation`.
 *                          Snaps actors into the picked formation.
 *   4. PlayCallPanel     — LEFT_RAIL; binds on
 *                          `domain=football && scene_type=play`.
 *                          Edits play call metadata.
 *   5. DefensiveFrontSpotlight — RIGHT_INSP; binds on
 *                          `domain=football && quiz_subject=defender_technique`.
 *                          Phase-12 DFQ legacy authoring helper.
 *
 * Each is intentionally a focused, well-bound DesignerTool. The
 * underlying behavior maps to existing football helper modules
 * (route_tree builders, coverage_catalog API, etc.) which P4
 * cutover hooks up.
 */
import React from 'react';

import { DesignerTool } from '../../DesignerTool';
import { useDesigner } from '../../DesignerContext';
import {
  all, requiresDomain, requiresCapability, requiresSceneType,
  requiresSubject,
} from '../../predicates';

// ────────────────────────────────────────────────────────────────────
// RouteAuthor — Konva pen on the offensive actor's path.
// ────────────────────────────────────────────────────────────────────

function RouteAuthor() {
  const { eventBus, selectedActorId } = useDesigner();
  return (
    <div
      className="designer-tool designer-tool--canvas-overlay-toolbar"
      data-tool-id="domain.football.route_author"
      role="toolbar"
      aria-label="Route author"
    >
      <button
        type="button"
        className="designer-tool__button designer-tool__button--icon"
        disabled={!selectedActorId}
        aria-label="Author route for selected actor"
        title="Pen route"
        onClick={() => eventBus.emit('football.route_tool_changed', { tool: 'pen' })}
      >✎</button>
      <button
        type="button"
        className="designer-tool__button designer-tool__button--icon"
        disabled={!selectedActorId}
        aria-label="Clear route"
        title="Clear route"
        onClick={() => eventBus.emit('football.route_tool_changed', { tool: 'clear' })}
      >⌫</button>
    </div>
  );
}

export const routeAuthor = new DesignerTool({
  id:         'domain.football.route_author',
  label:      'Route author',
  slot:       'CANVAS_OVERLAY',
  category:   'annotation',
  applies_to: all(
    requiresDomain('football'),
    requiresCapability('actor_movement'),
  ),
  Component:  RouteAuthor,
  weight:     105,
  helpText:   'Draw a route path for the selected receiver / defender.',
});

// ────────────────────────────────────────────────────────────────────
// CoveragePicker — RIGHT_INSP; pulls from coverage_catalog.
// ────────────────────────────────────────────────────────────────────

function CoveragePicker() {
  const { scene, setDomainMetadata } = useDesigner();
  if (!scene) return null;
  const meta = scene.domain_metadata || {};

  return (
    <div className="designer-tool" data-tool-id="domain.football.coverage_picker">
      <div className="designer-tool__row">
        <label className="designer-tool__label">Defensive coverage</label>
        <input
          type="text"
          className="designer-tool__input"
          value={meta.coverage_id || ''}
          aria-label="Coverage id"
          placeholder="e.g. cover_3"
          onChange={(e) => setDomainMetadata({ coverage_id: e.target.value })}
        />
      </div>
    </div>
  );
}

export const coveragePicker = new DesignerTool({
  id:         'domain.football.coverage_picker',
  label:      'Coverage',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: all(
    requiresDomain('football'),
    requiresCapability('needs_formation'),
  ),
  Component:  CoveragePicker,
  weight:     110,
  helpText:   'Pick the defensive coverage from the football coverage_catalog.',
});

// ────────────────────────────────────────────────────────────────────
// FormationOverlay — emits formation snap event.
// ────────────────────────────────────────────────────────────────────

function FormationOverlay() {
  const { scene, eventBus } = useDesigner();
  if (!scene) return null;

  return (
    <div
      className="designer-tool designer-tool--canvas-overlay-toolbar"
      data-tool-id="domain.football.formation_overlay"
      role="toolbar"
      aria-label="Formation snapping"
    >
      <button
        type="button"
        className="designer-tool__button designer-tool__button--icon"
        aria-label="Snap to formation"
        title="Snap actors to the selected formation"
        onClick={() => eventBus.emit('football.snap_formation', { scene })}
      >⚏</button>
    </div>
  );
}

export const formationOverlay = new DesignerTool({
  id:         'domain.football.formation_overlay',
  label:      'Formation overlay',
  slot:       'CANVAS_OVERLAY',
  category:   'annotation',
  applies_to: all(
    requiresDomain('football'),
    requiresCapability('needs_formation'),
  ),
  Component:  FormationOverlay,
  weight:     115,
  helpText:   'Snap offensive / defensive actors into the picked formation.',
});

// ────────────────────────────────────────────────────────────────────
// PlayCallPanel — LEFT_RAIL.
// ────────────────────────────────────────────────────────────────────

function PlayCallPanel() {
  const { scene, setDomainMetadata } = useDesigner();
  if (!scene) return null;
  const meta = scene.domain_metadata || {};

  return (
    <div className="designer-tool" data-tool-id="domain.football.play_call_panel">
      <div className="designer-tool__row">
        <label className="designer-tool__label">Play call</label>
        <input
          type="text"
          className="designer-tool__input"
          value={meta.play_call || ''}
          aria-label="Play call"
          placeholder="e.g. 21 Power"
          onChange={(e) => setDomainMetadata({ play_call: e.target.value })}
        />
      </div>
      <div className="designer-tool__row">
        <label className="designer-tool__label">Down &amp; distance</label>
        <input
          type="text"
          className="designer-tool__input"
          value={meta.down_distance || ''}
          aria-label="Down and distance"
          placeholder="e.g. 3rd & 5"
          onChange={(e) => setDomainMetadata({ down_distance: e.target.value })}
        />
      </div>
    </div>
  );
}

export const playCallPanel = new DesignerTool({
  id:         'domain.football.play_call_panel',
  label:      'Play call',
  slot:       'LEFT_RAIL',
  category:   'metadata',
  applies_to: all(
    requiresDomain('football'),
    requiresSceneType('play'),
  ),
  Component:  PlayCallPanel,
  weight:     105,
  helpText:   'Edit the play call, down & distance metadata.',
});

// ────────────────────────────────────────────────────────────────────
// DefensiveFrontSpotlight — DFQ legacy helper, RIGHT_INSP.
// ────────────────────────────────────────────────────────────────────

function DefensiveFrontSpotlight() {
  const { scene, setDomainMetadata } = useDesigner();
  if (!scene) return null;
  const meta = scene.domain_metadata || {};
  const cycle = meta.spotlight_cycle || [];

  const addStop = () => {
    setDomainMetadata({
      spotlight_cycle: [
        ...cycle,
        {
          actor_id: '',
          label: '',
          dwell_ms: 2000,
          question_template_id: '',
        },
      ],
    });
  };

  return (
    <div className="designer-tool" data-tool-id="domain.football.defensive_front_spotlight">
      <h3 className="designer-tool__heading">Spotlight cycle</h3>
      {cycle.map((stop, i) => (
        <div
          key={i}
          className="designer-tool__row designer-tool__row--inline"
        >
          <span className="designer-tool__chip">{stop.actor_id || '?'}</span>
          <span>{stop.label || '<no label>'}</span>
          <span>{stop.dwell_ms || 0} ms</span>
        </div>
      ))}
      <button
        type="button"
        className="designer-tool__button"
        onClick={addStop}
        aria-label="Append spotlight stop"
      >
        + Stop
      </button>
    </div>
  );
}

export const defensiveFrontSpotlight = new DesignerTool({
  id:         'domain.football.defensive_front_spotlight',
  label:      'Spotlight cycle',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: all(
    requiresDomain('football'),
    requiresSubject('defender_technique'),
  ),
  Component:  DefensiveFrontSpotlight,
  weight:     115,
  helpText:   'Define the per-defender spotlight cycle for DFQ-style quizzes.',
});

// ────────────────────────────────────────────────────────────────────
// Bulk export — football pack registers all five at boot.
// ────────────────────────────────────────────────────────────────────

export const footballDomainTools = [
  routeAuthor,
  coveragePicker,
  formationOverlay,
  playCallPanel,
  defensiveFrontSpotlight,
];

export default footballDomainTools;
